
import { authService } from './authService';
import { ACHIEVEMENTS, Achievement } from '../config/achievements';

export interface XPActivity {
  type: string;
  xp: number;
  description: string;
}

export const XP_TABLE: Record<string, number> = {
  reading_complete: 50,
  listening_complete: 50,
  writing_submit: 100,
  speaking_practice: 75,
  question_correct: 10,
  question_wrong: 2,
  combo_5x: 50,
  combo_10x: 100,
  perfect_score: 200,
  daily_streak: 100,
  achievement_unlock: 150
};

export const LEVEL_THRESHOLDS = [
  0, 100, 250, 450, 700, 1000, 1400, 1900, 2500, 3200, 
  4000, 5000, 6200, 7600, 9200, 11000, 13000, 15200, 17600, 20200
];

export const RANK_TITLES = [
  { level: 1, title: "RECRUIT", color: "#9ca3af" },
  { level: 5, title: "SOLDIER", color: "#3b82f6" },
  { level: 10, title: "SPECIALIST", color: "#8b5cf6" },
  { level: 15, title: "ELITE", color: "#C5A059" },
  { level: 20, title: "COMMANDER", color: "#ef4444" }
];

export class GamificationService {
  async awardXP(userId: string, activityType: string): Promise<{ 
    xpGained: number; 
    levelUp: boolean; 
    newLevel: number; 
  }> {
    const user = await authService.getUserById(userId);
    if (!user) return { xpGained: 0, levelUp: false, newLevel: 1 };
    
    const xpGained = XP_TABLE[activityType] || 0;
    const newXP = user.stats.xp + xpGained;
    const oldLevel = user.stats.level;
    const newLevel = this.calculateLevel(newXP);
    
    await authService.updateUser(userId, {
      'stats.xp': newXP,
      'stats.level': newLevel
    });
    
    // Notify UI
    window.dispatchEvent(new CustomEvent('xp-gained', { 
      detail: { xp: xpGained, activity: activityType } 
    }));
    
    if (newLevel > oldLevel) {
       window.dispatchEvent(new CustomEvent('level-up', { detail: { level: newLevel } }));
    }

    // Check achievements after XP update
    await this.checkAchievements(userId);

    return {
      xpGained,
      levelUp: newLevel > oldLevel,
      newLevel
    };
  }
  
  async checkAchievements(userId: string) {
    const user = await authService.getUserById(userId);
    if (!user) return;
    
    const userAchievements = new Set(user.achievements || []);
    const newUnlocked: Achievement[] = [];
    
    for (const achievement of ACHIEVEMENTS) {
      if (!userAchievements.has(achievement.id)) {
        if (achievement.condition(user)) {
          userAchievements.add(achievement.id);
          newUnlocked.push(achievement);
          
          // Award XP for achievement
          await this.awardXP(userId, 'achievement_unlock');
        }
      }
    }
    
    if (newUnlocked.length > 0) {
      await authService.updateUser(userId, {
        achievements: Array.from(userAchievements)
      });
      
      // Notify UI for each unlocked achievement
      newUnlocked.forEach(ach => {
        window.dispatchEvent(new CustomEvent('achievement-unlocked', { detail: ach }));
      });
    }
  }

  calculateLevel(xp: number): number {
    for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
      if (xp >= LEVEL_THRESHOLDS[i]) {
        return i + 1;
      }
    }
    return 1;
  }
  
  getRankInfo(level: number) {
    for (let i = RANK_TITLES.length - 1; i >= 0; i--) {
      if (level >= RANK_TITLES[i].level) {
        return RANK_TITLES[i];
      }
    }
    return RANK_TITLES[0];
  }
  
  getProgressToNextLevel(xp: number, level: number): number {
    const currentThreshold = LEVEL_THRESHOLDS[level - 1] || 0;
    const nextThreshold = LEVEL_THRESHOLDS[level] || (currentThreshold + 1000);
    const progress = xp - currentThreshold;
    const required = nextThreshold - currentThreshold;
    return Math.min(100, Math.max(0, (progress / required) * 100));
  }
}

export const gamificationService = new GamificationService();
